"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "../../atoms/button/button";
import styles from "./marquee-parallax-video.module.css";
import type { MarqueeParallaxVideoStage } from "./marquee-parallax-video.types";
import { withSafeRender } from "../../atoms/safe-render";

/** Names the control by what it does, not by the glyph it happens to show. */
export const PAUSE_LABEL = "Pause the video";
export const PLAY_LABEL = "Play the video";

/**
 * Slack on every width comparison, so a picture landing a rounding error off
 * its own published width still reads as having reached it.
 */
export const MEASURE_EPSILON_PX = 0.5;

/**
 * How far past a boundary the picture has to travel before the step behind it
 * is given up. ⚠ NOT A SECOND EPSILON: the slack above is symmetric, so a
 * picture resting a fraction of a pixel from a boundary steps back and forth
 * on any jitter — and every step swaps the medium and the playback with it.
 */
export const STAGE_DEADBAND_PX = 2;

/**
 * How many times over a focused element may be brought into view again before
 * the block stops trying.
 *
 * ⚠ NOT A RETRY COUNT: each pass answers the ramp's reply to the pass before
 * it, so the sequence converges rather than repeating.
 * ⚠ AND IT IS HEADROOM RATHER THAN A TUNED FIGURE, WHICH IS WHAT THE `center`
 * LANDING BELOW BUYS. Measured from the opening step across 36 viewport sizes
 * and both CTAs: at most one pass with the host's `scroll-behavior` unset and at
 * most two with its `smooth` declared, and none at all where the browser's own
 * focus scroll had already landed the button. What the cap is still for is a rect that never reads as
 * reached — the wording it chases is the author's — and the budget running out
 * is SAID rather than returned silently, so a size that needs more than this
 * reports itself instead of quietly leaving the button below the fold.
 */
export const FOCUS_SETTLE_PASSES = 8;

/** The setting that stops the shrink being scrubbed against the scroll. */
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** Below this a write moves nothing and the compositor still pays for it. */
const PROGRESS_STEP = 0.001;

/** One poster preload per width, gapless and mutually exclusive so exactly
 *  one is fetched — fractional widths between 1279 and 1280 included. */
const POSTER_WIDE_MEDIA = "(width >= 1280px)";
const POSTER_TABLET_MEDIA = "(735px <= width < 1280px)";
const POSTER_MOBILE_MEDIA = "(width < 735px)";

/** Below this the block rests at the card from the start and is not scrubbed;
 *  the stylesheet holds the picture there. ⚠ THE STYLESHEET'S OWN QUERY, word
 *  for word, so no fractional width runs the ramp under the held card. */
const NARROW_STATIC_MEDIA = "not all and (min-width: 1280px)";

/** What `written` holds while the narrow tiers have the ramp switched off. */
const RAMP_OFF = -2;

/**
 * `useLayoutEffect` in the browser and `useEffect` on the server, where the
 * first warns, so a client render measures before its first paint.
 *
 * ⚠ THE CLIENT RENDER ALONE, AND NOT A PROMISE OF NO FLASH AT ALL — a server
 * render emits `data-stage="rest"` and moves at hydration. See
 * design-conflicts/marquee-parallax-video.mdx.
 */
const useMeasureEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * Whether the reader has asked for less motion, read during the first render so
 * a block that must not scrub never gets a frame in which it does.
 *
 * ⚠ IT MUST DECIDE NOTHING THAT IS RENDERED, or the server's answer and the
 * browser's disagreeing is a hydration mismatch — which is why the flag the
 * two media are swapped on waits for `mounted`.
 * ⚠ GUARDED BECAUSE jsdom SHIPS NO matchMedia.
 */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
}

/**
 * What the engine publishes about the connection, or nothing where it publishes
 * none of it.
 *
 * ⚠ TYPED HERE BECAUSE TypeScript 4.9's DOM LIB PUBLISHES NO NetworkInformation
 * AT ALL, the same gap `popover.d.ts` fills for its own API — and every member
 * is optional on purpose, so a reading and a binding both have to be guarded.
 */
type Metering = {
  addEventListener?: (type: "change", listener: () => void) => void;
  removeEventListener?: (type: "change", listener: () => void) => void;
  saveData?: boolean;
};

function metering(): Metering | undefined {
  if (typeof navigator === "undefined") {
    return undefined;
  }

  return (navigator as Navigator & { connection?: Metering }).connection;
}

/**
 * Whether the reader has asked their browser to spend less data — Data Saver on
 * Android, Low Data Mode on iOS — which is the other answer the playback effect
 * declines an automatic start on.
 *
 * ⚠ IT MUST DECIDE NOTHING THAT IS RENDERED EITHER, for the reason above: the
 * setting is answerable in the browser alone, so it is read from script — the
 * seed the block opens with, and the effect that starts the film — and never
 * written onto the element.
 * ⚠ GUARDED BECAUSE `navigator.connection` IS NOT UNIVERSAL — Safari and
 * Firefox publish none of it, jsdom ships none, and no engine publishing it is
 * what "not metered" reads as.
 */
function savesData(): boolean {
  return metering()?.saveData === true;
}

/**
 * What each code MediaError publishes means, as the thing that went wrong
 * rather than as the constant's name.
 *
 * ⚠ THE SPEC'S OWN SET AND CLOSED AT FOUR, so a reading falling through is an
 * engine reporting something no standard defines rather than a gap here.
 */
const MEDIA_FAULTS: Record<number, string> = {
  1: "the fetch was stopped before it finished",
  2: "the network gave out part-way through it",
  3: "what arrived could not be decoded",
  4: "nothing at that address could be played at all, which is what a dead address looks like"
};

/**
 * Why the element stopped, with the code and whatever the engine said beside
 * it.
 *
 * ⚠ AND A MISSING FAULT IS ANSWERED RATHER THAN ASSUMED: `error` is null until
 * the element has one, and a `source` child that cannot be decoded never sets
 * it at all.
 */
function mediaFault(fault: MediaError | null): string {
  if (!fault) {
    return "the engine published no cause at all";
  }

  const cause = MEDIA_FAULTS[fault.code] ?? "the engine published a code no standard defines";
  const said = fault.message ? `, "${fault.message}"` : "";

  return `${cause} (code ${fault.code}${said})`;
}

/**
 * A film stopped for good, said where someone can act on it. ⚠ NOT GATED ON THE
 * BUILD, SO QUOTING NOTHING AUTHORED: a dead address looks like a refused start,
 * and only the load that failed knows it — hence here, not on the server.
 */
function reportDeadVideo(fault: MediaError | null, poster: string | undefined) {
  const remains = poster === undefined ? "an empty space" : "the poster";

  console.error(
    `[MarqueeParallaxVideo] the video stopped for good and only a ` +
      `press asks for it again: ${mediaFault(fault)}. What is left on the ` +
      `page — ${remains}, with the control offering play — is what a refused ` +
      `automatic start and a metered connection leave behind too, so this ` +
      `message is the only thing that tells the three apart.`
  );
}

/**
 * A per-tier width token as a share of the block, or NaN where it is not
 * readable.
 *
 * ⚠ NaN CARRIES TWO MEANINGS AND `stageFor` TELLS THEM APART: an unreadable
 * stop, and a `framed` band a tier declines to declare at all.
 * ⚠ AND NOT 0 FOR EITHER — zero is a width, and it opens the block at the step
 * that hides everything it has to say.
 */
function fraction(token: string): number {
  const parsed = Number.parseFloat(token);

  return Number.isFinite(parsed) ? parsed / 100 : Number.NaN;
}

/**
 * Which step of the storyboard a picture of this width is at.
 *
 * ⚠ EVERY BOUNDARY IS A WIDTH THE STYLESHEET DECLARES, and none is a scroll
 * offset.
 * ⚠ AN UNDECLARED `framed` STOP SKIPS THE BAND, IT DOES NOT DEGENERATE, and
 * the finite test below listing three stops rather than four is what does it:
 * add `framed` to that list and a tier declining the card would rest at its
 * opening width instead. Every tier declares one today, each by decision
 * rather than off a frame.
 * ⚠ SO `Number.isFinite(framed)` CHANGES NO ANSWER: `m <= NaN` is already
 * false, and finite or NaN are the only two `fraction` returns. It is kept so
 * the skip is stated rather than resting on NaN comparison semantics alone,
 * which the NaN-safe negated form this file uses twice for widths — `!(m > f)`
 * — silently inverts.
 * ⚠ AND A MEASUREMENT OR A STOP THAT IS NOT A NUMBER RESTS, because every other
 * step hides the block's wording.
 */
export function stageFor(
  media: number,
  framed: number,
  hardware: number,
  rest: number
): MarqueeParallaxVideoStage {
  if (![media, hardware, rest].every(Number.isFinite)) {
    return "rest";
  }

  if (media <= rest + MEASURE_EPSILON_PX) {
    return "rest";
  }

  if (media <= hardware + MEASURE_EPSILON_PX) {
    return "hardware";
  }

  if (Number.isFinite(framed) && media <= framed + MEASURE_EPSILON_PX) {
    return "framed";
  }

  return "full";
}

/**
 * The step the block settles on, given the one it is already showing. The
 * boundaries stay in `stageFor` alone, so moving one moves this with it.
 *
 * ⚠ THE DEADBAND IS ON THE WAY BACK ALONE, AND SUBTRACTING IS WHAT MAKES IT SO
 * — a direction test below would be the same rule written twice.
 * ⚠ AND THE TEST IS THAT THE OVERSHOOT HAS LEFT THE STEP ON SCREEN, not that it
 * has reached the new one, or a picture that jumps several bands sticks.
 */
export function settledStage(
  showing: MarqueeParallaxVideoStage,
  media: number,
  framed: number,
  hardware: number,
  rest: number
): MarqueeParallaxVideoStage {
  const next = stageFor(media, framed, hardware, rest);

  if (next === showing) {
    return showing;
  }

  const overshot = stageFor(media - STAGE_DEADBAND_PX, framed, hardware, rest);

  return overshot === showing ? showing : next;
}

/**
 * Whether this step draws the video rather than the equipment image. The swap
 * is which of the two is the medium at all rather than a fade between them, so
 * the control belongs to the steps that have a video to control.
 */
export function playsVideo(stage: MarqueeParallaxVideoStage): boolean {
  return stage === "full" || stage === "framed";
}

/** The play mark, drawn while the video is stopped. */
function PlayGlyph() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      height="100%"
      viewBox="0 0 24 24"
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.2493 11.1349C18.8322 11.5195 18.8322 12.4805 18.2493 12.8651L8.77473 19.1163C8.15597 19.5245 7.375 19.0269 7.375 18.2512V5.74878C7.375 4.97308 8.15597 4.47547 8.77473 4.88369L18.2493 11.1349Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** The pause mark: two bars, drawn to the control's own 24px box. */
function PauseGlyph() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      height="100%"
      viewBox="0 0 24 24"
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 5H10.5C10.7761 5 11 5.22386 11 5.5V18.5C11 18.7761 10.7761 19 10.5 19H9C8.72386 19 8.5 18.7761 8.5 18.5V5.5C8.5 5.22386 8.72386 5 9 5ZM13.5 5H15C15.2761 5 15.5 5.22386 15.5 5.5V18.5C15.5 18.7761 15.2761 19 15 19H13.5C13.2239 19 13 18.7761 13 18.5V5.5C13 5.22386 13.2239 5 13.5 5Z"
        fill="currentColor"
      />
    </svg>
  );
}

export interface MarqueeParallaxVideoClientProps {
  /**
   * The block's wording and its buttons, already rendered, slotted into the box
   * the ramp reveals. Absent, the box is still reserved.
   */
  children?: React.ReactNode;
  /** What a screen reader says in place of the equipment image; "" is
      reported. */
  equipmentAlt: string;
  /** The composite's address, or absent, in which case the video is the medium
      at every step. */
  equipmentSrc?: string;
  /** The bezel's address, or absent where the author supplied none. */
  frameSrc?: string;
  /**
   * What the block is called, which names its region. Absent — no heading to
   * take it from — and no region is drawn either.
   */
  label?: string;
  /**
   * What the block has been left to draw less of, written on the server on the
   * same terms as `problems`. ⚠ SAID AS A WARNING RATHER THAN AS AN ERROR: each
   * names a degradation the contract publishes and a story demonstrates, so red
   * would send an author to correct a shape they chose.
   */
  notices?: readonly string[];
  /**
   * A step to hold the block at, already resolved against the contract. Absent
   * — the ordinary case — the step is measured and the scroll drives it.
   */
  pinned?: MarqueeParallaxVideoStage;
  /**
   * The poster's address per width, already fenced for `url()`. Absent, the
   * native `poster` attribute carries `posterSrc` alone.
   */
  posterCrops?: { mobile: string; tablet: string; wide: string };
  /** A frame to show while the video is arriving, or absent for none. */
  posterSrc?: string;
  /**
   * What the block could not draw at all, written on the server. Absent in a
   * production build, and absent is also "nothing to say".
   */
  problems?: readonly string[];
  /** The video's accessible name, or absent where the author gave none. */
  videoLabel?: string;
  /** The video's address. "" draws nothing at all — see the guard below. */
  videoSrc: string;
}

/**
 * Marquee Parallax Video's client half: the measurement, the scroll scrub, the
 * play control and the swap between the two media. "use client" is earned by
 * RUNTIME MEASUREMENT, which layout resolves on both sides.
 *
 * ⚠ THE SCROLL WRITES NO STATE: `--mpv-progress` sweeps the whole ramp beside
 * the travel, the wording's height and the `data-travel` ask, and the stage is
 * the only part of it that is state.
 * ⚠ AND THE WORDING RISES WITHOUT BEING MOVED — the medium is in flow with the
 * wording after it, so do not add a translate for a distance layout knows.
 */
function MarqueeParallaxVideoClientImpl({
  children,
  equipmentAlt,
  equipmentSrc,
  frameSrc,
  label,
  notices,
  pinned,
  posterCrops,
  posterSrc,
  problems,
  videoLabel,
  videoSrc
}: MarqueeParallaxVideoClientProps) {
  const [measured, setMeasured] = useState<MarqueeParallaxVideoStage>("rest");
  const [reduced, setReduced] = useState(prefersReducedMotion);

  /* The reader's own answer to "should this be playing", which outlives every
     step. Starts on unless they have asked for less motion or for less data.
     ⚠ BOTH SETTINGS AND NOT ONE: each is answerable in the browser alone, so
     a seed reading only the first leaves the other's reader wanting the video
     until the scroll reaches a step the playback effect can decline at.
     Reading them here renders nothing on its own — `mounted` is what keeps
     the answer off the server's markup. */
  const [wanted, setWanted] = useState(() => !prefersReducedMotion() && !savesData());

  /* A stop this block did not issue — Low Power Mode, the OS media keys,
     Picture-in-Picture — held apart from the reader's own answer so the control
     cannot offer to pause a video that is already stopped.
     ⚠ AND NOT setWanted(false) FROM THE `pause` EVENT: this block pauses the
     element at every step that does not draw it, so reading its own pause as
     the reader changing their mind throws the answer away on the first scroll. */
  const [interrupted, setInterrupted] = useState(false);

  /* Whether the element itself reports frames advancing, which is the only
     answer the control's name is allowed to carry — with no `autoPlay` and
     `preload="none"` the film is still being fetched for seconds after
     `wanted` goes true, and a name taken from the answer instead reads "pause"
     over a poster nothing has replaced (WCAG 4.1.2).
     ⚠ SET FROM `playing` AND NOT FROM `play`: the second fires at the call
     itself, which is the claim this state exists to stop.
     ⚠ AND CLEARED ON `emptied` AND `error` AS WELL AS ON `pause`: those two are
     the stops that send no `pause` at all — the error steps leave `paused`
     false, and a film swapped out mid-play is paused by the load algorithm
     silently — so without them the name is left over a stopped element.
     ⚠ AND NOT CLEARED ON `waiting`: a stall part-way through a film is the one
     state every player still names "pause", and clearing it there renames the
     control under a reader who has not touched it. */
  const [advancing, setAdvancing] = useState(false);

  /* Whether the first render is behind us, which is what lets the reader's
     own answer and the interruption reach the flag the media are swapped on.
     See `plays`, at the foot of this component. */
  const [mounted, setMounted] = useState(false);

  /* Whether any of the block is on screen. Below 1280 the step never leaves
     the card, so this is what stops the film once the reader has scrolled on. */
  const [onScreen, setOnScreen] = useState(true);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const equipmentRef = useRef<HTMLImageElement>(null);

  /* The step on screen, beside the state that draws it: the measurement runs
     from inside a callback and cannot wait for a render to tell it. */
  const showing = useRef<MarqueeParallaxVideoStage>("rest");

  /* Whether the `pause` about to arrive is one this block asked for.
     ⚠ CLAIMED ONLY WHERE THE ELEMENT IS RUNNING: pause() on an already-paused
     element fires nothing, so a claim there stands until the next call and
     swallows the genuine interruption in between. */
  const selfPaused = useRef(false);

  /* Whether the reader has ever pressed the control, which is what tells an
     automatic start from one they asked for.
     ⚠ A REF RATHER THAN STATE: nothing renders from it, and the playback effect
     reads it on the pass the press causes. */
  const pressed = useRef(false);

  /* Whether the control is the thing the reader is standing on, which is what
     the step that takes it away needs in order to catch the focus it drops.
     ⚠ CLAIMED FROM THE ROW'S `focusin`, AND GIVEN UP ON A `focusout` THE
     CONTROL OUTLIVES BY A FRAME: the step taking the control fires one too —
     measured in Chrome at 375, 768 and 1440, arriving with the element still
     connected and the focus already on the body — so the event alone would
     clear the flag on the one path it exists for. A frame later the element
     the step took has left the document and the one a reader left has not,
     and telling those apart is what makes leaving of one's own accord
     clearable.
     ⚠ AND WHERE THE FOCUS WENT CANNOT DO IT: a click on blank space outside
     the block leaves it on the body, which is the answer the drop gives too. */
  const controlHeld = useRef(false);

  /* The frame that gives the claim above up, held so the focus coming back can
     call it off.
     ⚠ A HIDDEN DOCUMENT RUNS NO FRAMES AT ALL: the one queued when a tab switch
     blurs the control is still outstanding on the reader's return, lands behind
     the `focusin` that put them back on it, and would give up a claim they are
     standing on (WCAG 2.4.3, the drop the claim exists to catch). */
  const release = useRef(0);

  const showingStage = pinned ?? measured;

  /* ⚠ THE RESTING STEP DRAWS THE VIDEO WHEN A READER ON prefers-reduced-motion
     HAS ASKED FOR IT, AND ONLY THEN. That setting parks the block here for
     good, so hiding the video at this step as well would leave it with no route
     at all — see the same pair of rules in the stylesheet. */
  const askedForVideo = reduced && wanted;
  const drawsVideo = playsVideo(showingStage) || askedForVideo;

  /* ⚠ KEYED TO THE MESSAGES AND NOT TO THE ARRAY HOLDING THEM: a consumer
     writing a field inline hands a new array on every parent render, and an
     effect depending on it would repeat every message on each one. Encoded
     rather than joined, because a message quotes CMS wording back.
     ⚠ AND BOTH ARE READ OUT OF THE CLOSURE RATHER THAN DEPENDED ON, which the
     encoding is what makes safe. react-hooks/exhaustive-deps is off in this
     repo; checked by hand. */
  const reported = JSON.stringify([problems ?? [], notices ?? []]);

  useEffect(() => {
    problems?.forEach(problem => console.error(problem));
    notices?.forEach(notice => console.warn(notice));
  }, [reported]);

  useEffect(() => {
    setMounted(true);
  }, []);

  /* The setting can be turned on under a live block, and a picture frozen
     part-way through a shrink nothing will finish is the one outcome it must
     not leave. Turning it back off does not restart the video. */
  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => {
      setReduced(query.matches);

      if (query.matches) {
        setWanted(false);
      }
    };

    onChange();

    /* ⚠ A MediaQueryList IS NOT AN EventTarget ON SAFARI 13 AND OLDER EMBEDDED
       WEBVIEWS: `addEventListener` is absent and calling it throws from inside
       a committed effect. Remove the deprecated pair when a browserslist rules
       those engines out. */
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", onChange);

      return () => query.removeEventListener("change", onChange);
    }

    query.addListener(onChange);

    return () => query.removeListener(onChange);
  }, []);

  /* This setting can be turned on under a live block too, and the block that
     read "not metered" a moment ago is then spending the data it has just been
     told not to. Turning it back off does not restart the video, which is the
     query above and the same reason: only the reader restores what they
     stopped. The press that follows plays, as it does at an automatic start.
     ⚠ NO DEPRECATED PAIR BESIDE THE MODERN ONE, UNLIKE THE QUERY ABOVE: that
     form is MediaQueryList's alone, and an engine old enough to want it
     publishes no `connection` to bind to at all.
     ⚠ AND IT IS THE TURNING ON THAT IS ACTED ON, NOT THE READING: a
     NetworkInformation fires `change` for `effectiveType`, `rtt` and
     `downlink` as well, so a handler that only asked whether the setting is on
     would stop the film an already-metered reader pressed for on the next
     routine shift of their connection — which is what the playback effect's
     own rule below forbids. It is the READING that is latched and not the
     press, because a genuine flip stops the film whether the reader asked for
     it or not; that is this handler's whole case.
     ⚠ AND WHAT IS LEFT ON SCREEN IS THE LAST DECODED FRAME RATHER THAN THE
     POSTER: only load() puts a poster back, and it resets the element. See
     design-conflicts/marquee-parallax-video.mdx. */
  useEffect(() => {
    const connection = metering();

    if (typeof connection?.addEventListener !== "function") {
      return undefined;
    }

    let metered = savesData();

    const onChange = () => {
      const now = savesData();
      const turnedOn = now && !metered;

      metered = now;

      if (turnedOn) {
        setWanted(false);
      }
    };

    connection.addEventListener("change", onChange);

    return () => {
      connection.removeEventListener?.("change", onChange);
    };
  }, []);

  /* THE COMPOSITE'S OWN SHAPE, read off the file the CMS delivered.
     ⚠ NOT AN `onLoad` PROP: a cached image can be complete before React
     attaches the handler, and then nothing ever fires.
     ⚠ AND A FILE THAT NEVER DECODES LEAVES THE PUBLISHED FIGURE STANDING,
     because naturalWidth is 0 there — which is why the inline value is cleared
     and rewritten in one layout effect. */
  useMeasureEffect(() => {
    const root = rootRef.current;
    const image = equipmentRef.current;

    if (!root) {
      return undefined;
    }

    if (!image) {
      root.style.removeProperty("--mpv-composite-ratio");
      return undefined;
    }

    const write = () => {
      if (image.naturalWidth > 0 && image.naturalHeight > 0) {
        root.style.setProperty(
          "--mpv-composite-ratio",
          (image.naturalWidth / image.naturalHeight).toFixed(4)
        );
      }
    };

    root.style.removeProperty("--mpv-composite-ratio");
    write();
    image.addEventListener("load", write);

    return () => image.removeEventListener("load", write);
  }, [equipmentSrc, videoSrc]);

  /* THE SHRINK. Declared before the measurement below it so the first paint
     measures a medium the progress has already been applied to; layout effects
     run in the order they are written. */
  useMeasureEffect(() => {
    const root = rootRef.current;
    const scroller = scrollerRef.current;

    if (!root) {
      return undefined;
    }

    /* Pinned, stopped, or with no way to measure: the inline value is removed
       rather than overwritten, because it would beat the stylesheet's.
       ⚠ NO OBSERVER IS NOT A SLOWER SHRINK, IT IS A SILENT ONE — the medium
       would scrub the whole ramp while `data-stage` stayed at the resting step,
       so the ramp stops with the measurement. */
    if (
      pinned !== undefined ||
      reduced ||
      !scroller ||
      typeof ResizeObserver !== "function"
    ) {
      root.style.removeProperty("--mpv-progress");
      root.style.removeProperty("--mpv-content-block");
      scroller?.style.removeProperty("--mpv-travel");
      scroller?.removeAttribute("data-travel");
      return undefined;
    }

    /* The server's markup asks for it too, so nothing below moves at
       hydration. ⚠ STILL NEEDED: it puts the ask back after the branch above
       has removed it, and React will not, its prop being unchanged. */
    scroller.setAttribute("data-travel", "true");

    const narrow =
      typeof window.matchMedia === "function" ?
        window.matchMedia(NARROW_STATIC_MEDIA) :
        undefined;

    let frame = 0;
    let written = -1;
    let writtenTravel = 0;
    let writtenContent = -1;

    const read = () => {
      frame = 0;

      if (narrow?.matches) {
        if (written !== RAMP_OFF) {
          written = RAMP_OFF;
          writtenTravel = 0;
          writtenContent = -1;
          root.style.removeProperty("--mpv-progress");
          root.style.removeProperty("--mpv-content-block");
          scroller.style.removeProperty("--mpv-travel");
          scroller.removeAttribute("data-travel");
        }

        return;
      }

      if (written === RAMP_OFF) {
        written = -1;
        scroller.setAttribute("data-travel", "true");
      }

      /* ⚠ THE ONLY LAYOUT READS ON THE SCROLL PATH, with the write after them:
         the other order forces a synchronous layout on every wheel notch.
         ⚠ THE TRAVEL IS THE ICB'S HEIGHT, NOT window.innerHeight, which
         changes every time the URL bar slides. The box under the block is
         written from the same figure, so the block unpins exactly at 1. */
      const box = scroller.getBoundingClientRect();
      const travel = document.documentElement.clientHeight;

      if (!(travel > 0)) {
        return;
      }

      const progress = clamp01(-box.top / travel);

      /* The wording's height, so the stylesheet can centre the picture and the
         wording together at desktop. Read before the writes below. */
      const contentBlock = contentRef.current?.offsetHeight ?? 0;

      /* ⚠ THE TRAVEL TOO: a rotation at either end of the ramp moves no
         progress, and the box under the block would keep the old screen's. */
      if (
        Math.abs(progress - written) < PROGRESS_STEP &&
        travel === writtenTravel &&
        contentBlock === writtenContent
      ) {
        return;
      }

      written = progress;
      writtenTravel = travel;
      writtenContent = contentBlock;
      root.style.setProperty("--mpv-content-block", `${contentBlock}px`);
      root.style.setProperty("--mpv-progress", progress.toFixed(4));
      scroller.style.setProperty("--mpv-travel", `${travel}px`);
    };

    /* Coalesced: a wheel or a touch drag fires far more often than the screen is
       painted, and every one of those would otherwise be a read. */
    const schedule = () => {
      if (!frame) {
        frame = requestAnimationFrame(read);
      }
    };

    read();
    /* ⚠ THE FOCUS SETTLE LOOP IS ORDERED AGAINST THIS REGISTRATION: its pass
       waits two frames because the read below is asked for from `scroll`, so
       moving it to `scrollend`, an IntersectionObserver or a ResizeObserver
       leaves that loop measuring pre-ramp layout with every test still green. */
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);

      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [pinned, reduced, videoSrc]);

  /* THE MEASUREMENT: the medium's own drawn width against the widths its tier
     publishes, observed rather than derived back out of the ramp that set it.
     ⚠ ESLINT CHECKS NONE OF THESE DEPENDENCY ARRAYS — exhaustive-deps is off in
     this repo and would not know `useMeasureEffect`. Checked by hand. */
  useMeasureEffect(() => {
    const root = rootRef.current;
    const media = mediaRef.current;

    if (!root || !media) {
      return undefined;
    }

    /* ⚠ NOTHING IS MEASURED WHILE THE STEP IS PINNED, the same gate the shrink
       above carries: `pinned ?? measured` discards every answer this could
       reach. Unpinning re-runs it, and the seed below lands in the same
       commit's layout phase. */
    if (pinned !== undefined) {
      return undefined;
    }

    if (reduced) {
      showing.current = "rest";
      setMeasured("rest");
      return undefined;
    }

    /* ⚠ GUARDED BECAUSE jsdom SHIPS NO ResizeObserver: unguarded this throws in
       any consumer's test. Without one the block stays at the resting stage the
       stylesheet already draws. */
    if (typeof ResizeObserver !== "function") {
      return undefined;
    }

    let blockWidth = 0;
    let mediaWidth = 0;
    let framed = Number.NaN;
    let hardware = Number.NaN;
    let rest = Number.NaN;

    /* Read off the stylesheet rather than restated, so a frame is corrected in
       one place.
       ⚠ A STOP THE TIER DOES NOT DECLARE READS "" AND ANSWERS NaN rather than
       throwing — see `stageFor`. All three are declared at every tier today.
       ⚠ AND THIS RE-READS ON THE BLOCK'S WIDTH WHERE THE STOPS MOVE AT THE
       VIEWPORT'S, so a block in a narrower column can leave them a tier
       stale. Every consumer today is full-bleed. */
    const readStops = () => {
      const computed = window.getComputedStyle(root);

      framed = fraction(computed.getPropertyValue("--mpv-media-framed"));
      hardware = fraction(computed.getPropertyValue("--mpv-media-hardware"));
      rest = fraction(computed.getPropertyValue("--mpv-media-rest"));
    };

    const resolve = () => {
      /* ⚠ FINITE AND POSITIVE, NOT MERELY POSITIVE: every comparison against
         NaN is false, so a bare `<= 0` lets one through to the cascade. */
      if (!(blockWidth > 0) || !(mediaWidth > 0)) {
        return;
      }

      const next = settledStage(
        showing.current,
        mediaWidth,
        blockWidth * framed,
        blockWidth * hardware,
        blockWidth * rest
      );

      showing.current = next;
      setMeasured(next);
    };

    const observer = new ResizeObserver(entries => {
      entries.forEach(entry => {
        if (entry.target === root) {
          blockWidth = entry.contentRect.width;
          readStops();
          return;
        }

        mediaWidth = entry.contentRect.width;
      });

      resolve();
    });

    /* Measured here rather than waiting for the observer's first delivery: a
       state change made inside that callback need not be flushed before the
       frame is painted.
       ⚠ THE SAME BOX THE OBSERVER REPORTS, WHICH getBoundingClientRect IS NOT
       — that is the border box, and the two part company the day either element
       gains padding. */
    blockWidth = root.clientWidth;
    mediaWidth = media.clientWidth;
    readStops();
    resolve();

    observer.observe(root);
    observer.observe(media);

    return () => observer.disconnect();
  }, [pinned, reduced, videoSrc]);

  /* FINISHING THE SCROLL THE BROWSER STARTS WHEN SOMETHING IN HERE TAKES FOCUS.
     ⚠ ONE SCROLL CANNOT LAND IT: the browser reads a target off the layout
     before the scroll, that scroll advances --mpv-progress, the ramp re-lays out
     the medium and the wording under it, and nothing iterates — so the first Tab
     into the block left the button it reached below the fold at every desktop
     width (WCAG 2.4.11). Each pass here answers the move the last one caused.
     ⚠ AND IT RENDERS NOTHING AND DECIDES NOTHING, which is the invariant above
     `prefersReducedMotion`: no state, no attribute, no class — scrolling only.
     ⚠ `focusin` DOES NOT MEAN FOCUS MOVED: a `focus()` call on the element that
     already holds it fires nothing, but a window regaining focus fires one at
     whatever still holds it — which `onFocusIn` below has to tell from a Tab.
     ⚠ AND THE TWO-FRAME WINDOW IS ORDERED AGAINST THE RAMP'S OWN SCROLL-DRIVEN
     READ, so moving that read off `scroll` regresses this silently. */
  useEffect(() => {
    const root = rootRef.current;

    if (!root) {
      return undefined;
    }

    let frame = 0;
    let passes = 0;
    let target: Element | null = null;

    /* Whether the document has this moment regained focus, and the element this
       loop last watched take it. Neither alone separates a re-activation from a
       Tab; see `onFocusIn`. */
    let activation = 0;
    let activating = false;
    let held: Element | null = null;

    /* ⚠ EITHER WAY OF GIVING UP SAYS SO RATHER THAN RETURNING SILENTLY,
       because "settled" and "gave up 148px short" are otherwise the same
       observable and a reader cannot tell them apart. The two are not the same
       failure and the reason separates them: the budget has at least spent
       every pass it had, where the scroll having nothing left to give stops
       after ONE and is the class that leaves the button 137 and 191px below
       the fold.
       ⚠ AND IT IS WRITTEN HERE RATHER THAN COLLECTED WITH `problems` ON THE
       SERVER: nothing about it is knowable before layout. It keeps that
       channel's two conventions — the block's own name, and silence in a
       production build — and takes the ERROR half of the split, because what
       it reports is a control the reader cannot see (WCAG 2.4.11) rather than
       something the block drew less of.
       ⚠ AND WHAT IT NAMES IS THE ROOM THE STOP TEST ASKED FOR, RING INCLUDED,
       RATHER THAN THE BARE RECT: a box clipped only by its ring's room is
       nothing at all against the rect, which would report the one give-up the
       engine actually reaches as no shortfall. Two decimals and not rounded for
       the same reason — the residual it declines to scroll is a fraction of a
       pixel. */
    const giveUp = (shortfall: number, why: string) => {
      if (process.env.NODE_ENV === "production") {
        return;
      }

      console.error(
        `[MarqueeParallaxVideo] the focused element is still ` +
          `${Number(shortfall.toFixed(2))}px short of the room the scroll ` +
          `reserves for it, its focus ring included, at ` +
          `${window.innerWidth}x${window.innerHeight}, and ${why} — so it is ` +
          `left where the last pass put it. Nothing an author can fill in ` +
          `changes this: it is the block's own defect and not the page's ` +
          `content.`
      );
    };

    /* ⚠ TWO FRAMES AND NOT ONE, because the ramp writes --mpv-progress from a
       scroll-driven frame of its own and nothing orders the two: measured one
       frame on, this reads the layout the last scroll was computed FROM and
       scrolls again when it need not.
       ⚠ AND `settle` BELOW IS A DECLARATION RATHER THAN A CONST BECAUSE THE TWO
       CALL EACH OTHER: as a const it is in its temporal dead zone here. */
    const schedule = () => {
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(settle);
      });
    };

    function settle() {
      frame = 0;

      /* Focus has moved on, and chasing where it used to be would take the page
         away from wherever the reader is now. */
      if (target === null || target !== document.activeElement) {
        return;
      }

      const box = target.getBoundingClientRect();

      /* ⚠ THE ROOM THE SCROLL ITSELF HONOURS, AND THE BARE RECT IS NOT IT: the
         focused box carries a `scroll-margin` for its ring, so a test on the
         rect alone settles with that ring cut — 4px of it, by construction.
         Read physical, because the stylesheet declares the logical property and
         layout is what resolves one into the other. */
      const style = window.getComputedStyle(target);
      const above = box.top - (Number.parseFloat(style.scrollMarginTop) || 0);
      const below = box.bottom + (Number.parseFloat(style.scrollMarginBottom) || 0);

      /* One number rather than two, because the same quantity is both the stop
         test and what a give-up below reports. */
      const shortfall = Math.max(-above, below - window.innerHeight);

      if (shortfall <= 0) {
        return;
      }

      /* ⚠ GUARDED BECAUSE jsdom SHIPS NO scrollIntoView, the same shape as the
         observer and the media query above. */
      if (typeof target.scrollIntoView !== "function") {
        return;
      }

      if (passes === 0) {
        giveUp(shortfall, `the budget of ${FOCUS_SETTLE_PASSES} passes is spent`);

        return;
      }

      passes -= 1;

      const from = window.scrollY;

      /* ⚠ `html`'s OWN `scroll-behavior` IS SUPPRESSED ACROSS THE CALL AND PUT
         BACK INSIDE IT: every entry point this ships into declares `smooth`
         there, which makes the scroll asynchronous — the loop then measures the
         layout it started from and never iterates — and under
         prefers-reduced-motion animates a scroll foundation.css cannot reach,
         that rule being scoped to `[data-dds-root]` where the box is `html`.
         ⚠ `html` AND NOT "THE SCROLLING BOX", WHICH IS THE SAME ELEMENT ONLY
         WHERE THE VIEWPORT IS THE SCROLLER — see the stop test below.
         ⚠ AND NOT behavior: "instant", which an engine that has not shipped it
         reads as an unrecognised enum value and throws a TypeError over. */
      const viewport = document.documentElement;
      const behavior = viewport.style.scrollBehavior;

      viewport.style.scrollBehavior = "auto";

      /* ⚠ `center`, NOT `nearest`: aimed at an edge, the ramp's re-layout
         diverges in the `framed` band (design-conflicts has the figures).
         Restored from `finally` so a throw cannot leave the host on `auto`. */
      try {
        target.scrollIntoView({
          block: "center",
          inline: "nearest"
        });
      } finally {
        viewport.style.scrollBehavior = behavior;
      }

      /* Nothing moved, so there is nothing left to give — the document's own end
         rather than a pass that has not landed yet, and another would spin.
         ⚠ BOTH, BECAUSE EITHER ALONE STOPS A PASS THAT HAS LANDED: `.root` is
         sticky, so while the block is pinned a scroll that made progress leaves
         the target's box exactly where it was — and nested in a scroll container
         the element moves where the page does not.
         ⚠ AND THAT SECOND CASE REACHES FURTHER THAN THE SUPPRESSION ABOVE DOES,
         WHICH IS AN ASYMMETRY RATHER THAN AN OVERSIGHT: the write is on `html`,
         so a consumer nesting this block in its own scroll container — or a host
         declaring `scroll-behavior: smooth !important`, which an inline
         non-important write does not outrank — leaves the scroll animated and
         the loop back to measuring the layout it started from. Neither is
         reachable while every consumer is a full-bleed page section. */
      if (
        window.scrollY === from &&
        target.getBoundingClientRect().top === box.top
      ) {
        /* The shortfall above still stands, which is what nothing having moved
           means. */
        giveUp(shortfall, "the scroll has nothing left to give");

        return;
      }

      schedule();
    }

    const onWindowFocus = () => {
      activating = true;

      if (activation) {
        cancelAnimationFrame(activation);
      }

      activation = requestAnimationFrame(() => {
        activation = 0;
        activating = false;
      });
    };

    /* ⚠ A WINDOW REGAINING FOCUS ARRIVES HERE TOO, at whatever still held focus,
       and settling on that takes the page back from wherever the reader scrolled
       to. `relatedTarget` cannot separate them — null on an activation AND on
       the first Tab in from the browser chrome — and neither can the flag alone,
       because window focus also fires on first entry into the document, which is
       the Tab this loop exists for. It takes both: an activation in flight AND a
       target this loop has already watched take focus. */
    const onFocusIn = (event: FocusEvent) => {
      const focused = event.target as Element;

      /* ⚠ THE BLOCK'S OWN ROOT IS NEVER A TAB ARRIVING: it is no tab stop, and
         what puts the focus on it is the step unmounting the control under the
         reader — see the transfer below. A pass aimed at a box the size of the
         block would take the page away from the scroll that caused that step,
         and a click landing on it would scroll the page for a pointer. */
      if (focused === root) {
        return;
      }

      if (activating && focused === held) {
        return;
      }

      held = focused;
      target = focused;
      passes = FOCUS_SETTLE_PASSES;

      /* ⚠ THE TWO FRAMES RESTART ON EVERY ARRIVAL: a chain already one frame in
         leaves `settle` measuring the layout the browser's own focus scroll was
         computed from, which is the single-frame failure above. `frame` holds
         the one outstanding handle, so cancelling here cannot leak. */
      if (frame) {
        cancelAnimationFrame(frame);
      }

      schedule();
    };

    root.addEventListener("focusin", onFocusIn);
    /* Not captured, or every element `focus` on the way down reaches this too. */
    window.addEventListener("focus", onWindowFocus);

    return () => {
      root.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("focus", onWindowFocus);

      if (frame) {
        cancelAnimationFrame(frame);
      }

      if (activation) {
        cancelAnimationFrame(activation);
      }
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;

    if (!root || typeof IntersectionObserver !== "function") {
      return undefined;
    }

    /* The last entry: one callback can queue several for a fast fling. */
    const observer = new IntersectionObserver(entries => {
      setOnScreen(entries[entries.length - 1].isIntersecting);
    });

    observer.observe(root);

    return () => observer.disconnect();
  }, []);

  /* PLAYBACK, kept in step with which of the two media is drawn. Both stay
     mounted, or the video restarts on every scroll back up.
     ⚠ .play() RETURNS A PROMISE THAT REJECTS UNDER AUTOPLAY POLICY, so it is
     caught and the control offers `play` rather than lying.
     ⚠ AND A REJECTION IS NOT ALWAYS A REFUSAL: pause() rejects a play() in
     flight with AbortError, which is this effect's own pause on the next step.
     `live` tells the two apart. */
  useEffect(() => {
    const element = videoRef.current;

    if (!element) {
      return undefined;
    }

    if (!drawsVideo || !wanted || interrupted || !onScreen) {
      selfPaused.current = !element.paused;
      element.pause();

      /* An interruption belongs to the step it happened at: below the steps
         that draw the video it has nothing left to report on, and the reader's
         own answer governs again on the way back up. */
      if (interrupted && !drawsVideo) {
        setInterrupted(false);
      }

      return undefined;
    }

    /* ⚠ A METERED CONNECTION IS ANSWERED HERE AND NOT ON THE ELEMENT, WHICH IS
       WHAT HAVING NO `autoPlay` BELOW BUYS: the automatic start is this
       effect's to make, so nothing has begun loading by the time the setting is
       read. Declining lands the refused-autoplay end state — the control
       offering `play`, over the poster nothing has yet replaced — rather than a
       second one of its own, and it declines the film's bytes with it, because
       the element is authored `preload="none"` and this call is the only thing
       that ever asks it for more.
       ⚠ AND ONLY AN AUTOMATIC START IS DECLINED: refusing the reader's own press
       would leave a control that names `play` and does nothing (WCAG 4.1.2). */
    if (!pressed.current && savesData()) {
      setWanted(false);

      return undefined;
    }

    let live = true;
    const started = element.play();

    if (started) {
      started.catch(() => {
        if (live) {
          setWanted(false);
        }
      });
    }

    return () => {
      live = false;
    };
  }, [drawsVideo, interrupted, onScreen, wanted]);

  /* WHETHER THE BLOCK DRAWS A CONTROL AT ALL, which is whether a press would do
     anything: the two steps below the card hold the video stopped — whether or
     not there is a composite to put in its place — so a button there names an
     action it cannot perform however it is painted (WCAG 4.1.2). 2.2.2 is
     answered by the steps that DO play, which is where the control is drawn.
     ⚠ THE STEP AND NOT `drawsVideo`: under the reduce query a press that pauses
     takes `askedForVideo` false with it, so a control gated on the medium would
     leave on the press that stopped the film and give the reader no way to
     start it again.
     ⚠ AND `reduced` WAITS FOR `mounted` FOR THE REASON `plays` DOES — the
     setting is answerable in the browser alone, so a control the server drew
     none of appearing in the first client render is a hydration mismatch. The
     server's own answer is the step, which it does render. */
  const offersControl =
    playsVideo(showingStage) || (mounted && reduced && showingStage === "rest");

  /* CATCHING THE FOCUS THE STEP WOULD OTHERWISE DROP. A reader can be standing
     on the control when the storyboard steps past it — arrow keys scroll a
     focused button — and an element removed while it holds the focus leaves the
     document holding none: the next Tab resumes after the block, passing both
     buttons with no ring shown anywhere (WCAG 2.4.3).
     ⚠ THE BLOCK'S ROOT AND NOT THE CONTROL'S ROW, THOUGH BOTH SIT AHEAD OF THE
     BUTTONS NOW: the row carries no `tabindex` at all, so a focus() call on it
     lands nowhere and the drop would stand. The root is focusable by script and
     sits ahead of every button in the block, and with the heading filled in —
     which the field report demands of every author — it announces the region's
     own name. With it empty the block has no region and no name either, so
     what arrives is an unnamed box: the same nothing the drop announced, with
     the reader's place kept. `tabindex` is deliberately not gated on the name,
     which would put the drop back in the one configuration holding least.
     ⚠ `preventScroll` BECAUSE THE STEP ONLY CHANGES UNDER A SCROLL: the reader
     is mid-gesture, and a focus scroll answering it fights their own.
     ⚠ AND IT TAKES BOTH THE FLAG AND THE BODY, NEITHER ALONE: the flag says the
     control had the focus and still has it — the row gives it up for a reader
     who left by any route the browser reports — and the body holding the focus
     rules out the ordinary scroll past the block, where nothing in here was
     ever focused at all.
     ⚠ AND NOT `useMeasureEffect`: nothing here measures, and a layout effect is
     what makes the server renderer warn about an effect it cannot run. */
  useEffect(() => {
    const root = rootRef.current;

    if (offersControl || !controlHeld.current) {
      return;
    }

    controlHeld.current = false;

    if (document.activeElement !== document.body) {
      return;
    }

    /* ⚠ AND A REFUSAL ENDS IT RATHER THAN BEING RETRIED, which is what
       `standOn` in parallax-marquee does with the same silent answer: a claim
       kept past the transition that made it belongs to no reader, so the next
       step down would take the focus from someone who was never standing on
       the control — the one thing this must not do. There is no borrowed
       `tabindex` to hand back either, the root carrying its own. */
    root?.focus({ preventScroll: true });
  }, [offersControl]);

  /* ⚠ AND THE ROW'S OUTSTANDING FRAME GOES WITH THE BLOCK: it reads the
     document and writes the claim, both of which outlive a component that has
     left the tree. */
  useEffect(
    () => () => {
      cancelAnimationFrame(release.current);
    },
    []
  );

  /* ⚠ THE VIDEO IS THE BLOCK'S SUBJECT: with none there is nothing to draw,
     where a missing equipment image degrades instead.
     ⚠ AND IT IS ANSWERED HERE RATHER THAN ON THE PUBLISHED SURFACE, though the
     field is that surface's to judge — returning before this component renders
     would drop the message that says why the block is missing. */
  if (videoSrc === "") {
    return null;
  }

  /* Which medium the block draws and means to run, which is the flag the
     reduce query swaps the two on — so it has to be true BEFORE the element
     can start, and cannot wait on the element's own report.
     ⚠ THE STEP ALONE UNTIL THE FIRST RENDER IS BEHIND US, which is the whole of
     the rule above `prefersReducedMotion`: both `reduced` and `wanted` are read
     off answers the server does not have, so either one reaching this
     expression leaves the two sides drawing different media. */
  const plays =
    mounted ? drawsVideo && wanted && !interrupted : playsVideo(showingStage);

  /* What the control may claim, and what the reduce query gives the equipment
     image up on: the medium above AND the element's own word for it. Neither
     half carries it alone — the flag names `pause` over a poster for as long as
     the film takes to arrive, and the element's word names it over a video
     still decoding at a step that draws the equipment image. False on the
     server for every reader, which is why this one needs
     no `mounted`: with no `autoPlay` no frame can arrive before hydration. */
  const running = plays && advancing;

  /* The two stops that send no `pause` at all, and the reader's own answer goes
     with the element's word rather than the word alone: clearing the report
     alone renames the control to `play` and leaves the press behind it doing
     nothing, because `wanted` still holds the value that press would write and
     the effect's deps never move (WCAG 4.1.2, permanent after an error).
     ⚠ SO THE PRESS RETRIES RATHER THAN BEING WITHHELD: `emptied` is no error at
     all, only an undecodable source is certainly fatal, and where the retry
     cannot work play() rejects into the refusal the effect above answers. */
  const endPlayback = () => {
    setAdvancing(false);
    setWanted(false);
  };

  /* The poster paints only where the video is the medium: a pinned step below
     the card with a composite never draws it, and unpinned, reduced motion
     parks a desktop block at the composite. Decided from props so the server
     emits it. ⚠ BELOW 1280 THAT LEAVES THE FILM'S POSTER UNPRELOADED under
     reduced motion: painted on request rather than ahead of it. */
  const preloadsPoster =
    posterSrc !== undefined &&
    (pinned === undefined || playsVideo(pinned) || equipmentSrc === undefined);
  const posterMedia =
    pinned === undefined && equipmentSrc !== undefined ?
      "(prefers-reduced-motion: no-preference)" :
      undefined;

  /* ⚠ WITH CROPS THE POSTER IS THE VIDEO'S OWN BACKGROUND, NOT ITS ATTRIBUTE:
     `poster` takes one address, and a preload it cannot match is fetched twice.
     One preload per width, each at exactly the address the stylesheet asks for. */
  const posterPreloads =
    posterCrops === undefined ?
      [{
        href: posterSrc,
        media: posterMedia
      }] :
      [
        {
          href: posterCrops.wide,
          media: POSTER_WIDE_MEDIA
        },
        {
          href: posterCrops.tablet,
          media: POSTER_TABLET_MEDIA
        },
        {
          href: posterCrops.mobile,
          media: POSTER_MOBILE_MEDIA
        }
      ]
        .map(({ href, media }) => ({
          href,
          media: posterMedia === undefined ? media : `${posterMedia} and ${media}`
        }))
        /* Tiers that fall back to one address share one preload: its widths
           join as a media list, so the file is still fetched once per width. */
        .reduce<{ href: string; media: string }[]>((merged, preload) => {
          const same = merged.find(entry => entry.href === preload.href);
          if (same) {
            same.media = `${same.media}, ${preload.media}`;
          } else {
            merged.push({ ...preload });
          }
          return merged;
        }, []);
  const posterStyle =
    posterCrops === undefined ?
      undefined :
      ({
        "--mpv-poster-mobile": `url("${posterCrops.mobile}")`,
        "--mpv-poster-tablet": `url("${posterCrops.tablet}")`,
        "--mpv-poster-wide": `url("${posterCrops.wide}")`
      } as React.CSSProperties);

  return (
    <div
      className={styles.scroller}
      data-travel={pinned === undefined ? "true" : undefined}
      ref={scrollerRef}
    >
      {/* ⚠ THE NAME IS THE HEADING'S OWN WORDING RATHER THAN aria-labelledby:
          the published half is a Server Component, where useId cannot run, and
          an id fixed by hand collides the day a page draws two of these.
          ⚠ AND NO ROLE AT ALL WITHOUT ONE — an unnamed region is a landmark a
          reader cannot tell from any other.
          ⚠ EVERY FLAG IS ABSENT RATHER THAN "false", so the stylesheet has one
          state to match and not two: `data-plays` draws the video at the
          resting step under prefers-reduced-motion, `data-running` is what
          gives the equipment image up from under it — the intent and the report
          are not interchangeable there, see the stylesheet —
          `data-has-equipment` scopes the medium swap, and `data-has-frame`
          keeps the video's inset honest.
          ⚠ AND THE REPORT IS NOT NAMED `data-playing`, WHICH `marquee-media`
          ALREADY PUBLISHES FOR THE INTENT — this block's `data-plays`, and
          written `"false"` there rather than left off. One name carrying two
          opposite states is the clash; see design-conflicts.
          ⚠ AND IT IS FOCUSABLE BY SCRIPT ALONE: `-1` and never a positive
          value, so the tab order stays the DOM's, and it is where the transfer
          above puts a focus the step would otherwise drop. */}
      <div
        aria-label={label}
        className={styles.root}
        data-plays={plays ? "true" : undefined}
        data-running={running ? "true" : undefined}
        data-stage={showingStage}
        data-has-frame={frameSrc === undefined ? undefined : "true"}
        data-has-equipment={equipmentSrc === undefined ? undefined : "true"}
        ref={rootRef}
        role={label === undefined ? undefined : "region"}
        tabIndex={-1}
      >
        <div className={styles.media} ref={mediaRef}>
          {/* ⚠ THE POSTER IS THIS BLOCK'S LCP CANDIDATE: preloaded at the
              element's own address, `crossOrigin` on neither, or it is fetched
              twice. camelCase: the app's React 19 takes it; jest's 18.3.1 warns. */}
          {preloadsPoster &&
            posterPreloads.map(({ href, media }) => (
              <link
                as="image"
                fetchPriority="high"
                href={href}
                key={media ?? "all"}
                media={media}
                rel="preload"
              />
            ))}

          {/* ⚠ NO `autoPlay`, AND `preload="none"`: either starts the load before
              script can read the data-saver setting, so the playback effect
              starts it. `src` is on the element because a failing `source`
              child fires no `error`; only `error` is reported, since every
              ordinary source swap sends `emptied`. */}
          <video
            aria-label={videoLabel}
            className={styles.video}
            data-poster-crops={posterCrops === undefined ? undefined : "true"}
            loop
            muted
            onEmptied={endPlayback}
            onError={event => {
              reportDeadVideo(event.currentTarget.error, posterSrc);
              endPlayback();
            }}
            onPause={() => {
              setAdvancing(false);

              if (selfPaused.current) {
                selfPaused.current = false;
                return;
              }

              setInterrupted(true);
            }}
            onPlay={() => setInterrupted(false)}
            onPlaying={() => setAdvancing(true)}
            playsInline
            poster={posterCrops === undefined ? posterSrc : undefined}
            preload="none"
            ref={videoRef}
            src={videoSrc}
            style={posterStyle}
          />

          {/* ⚠ ONE RIGID PICTURE HOLDING THE TELEVISION AND THE EQUIPMENT
              TOGETHER, which cannot be right at both steps that draw it: it
              rides the television's width ramp, so the equipment is off at one
              of them. Do not add a second picture — see
              design-conflicts/marquee-parallax-video.mdx, which also records
              the one asset change that IS wanted: a REPLACEMENT carrying no
              television of its own, not a second element beside this one. */}
          {equipmentSrc !== undefined && (
            <img
              alt={equipmentAlt}
              className={styles.equipment}
              ref={equipmentRef}
              src={equipmentSrc}
            />
          )}

          {/* ⚠ DECORATION OVER A PICTURE THAT IS ALREADY DESCRIBED, SO `alt` IS
              EMPTY RATHER THAN ABSENT — a name here announces the same picture
              twice. It is one image over both media, so both read as the same
              television: the composite carries a set of its own and it is NOT
              registered with the video's, which is why no blend of the two
              works — design-conflicts/marquee-parallax-video.mdx. */}
          {frameSrc !== undefined && (
            <img alt="" className={styles.frame} src={frameSrc} />
          )}
        </div>

        {/* ⚠ AHEAD OF THE WORDING IN DOM ORDER, so a first Tab reaches the
            control rather than scrolling to a CTA and stepping it away. The row
            outlives the control, so its handlers hold when a step removes it. */}
        <div
          className={styles.controls}
          onBlur={event => {
            /* ⚠ DECIDED A FRAME LATER: a removed button fires `focusout` while
               still connected, and only a frame on is it gone. A hidden
               document (tab switch) is not a reader leaving. */
            const control = event.target as Element;

            release.current = requestAnimationFrame(() => {
              release.current = 0;

              if (
                document.hasFocus() &&
                control.isConnected &&
                document.activeElement !== control
              ) {
                controlHeld.current = false;
              }
            });
          }}
          onFocus={() => {
            cancelAnimationFrame(release.current);
            release.current = 0;
            controlHeld.current = true;
          }}
        >
          {/* The name carries the state, so there is no `pressed` beside it —
              "Play, pressed" reads two contradictory ways.
              ⚠ THE PRESS ANSWERS THE NAME ON THE CONTROL, NOT THE LAST ANSWER
              GIVEN: the two part company after an interruption, and again
              while a start is still arriving — where a press repeats the
              answer rather than cancelling it, because the name says `play`
              and stopping the film is the one thing that name does not say. */}
          {offersControl && (
            <Button
              ariaLabel={running ? PAUSE_LABEL : PLAY_LABEL}
              className={styles.control}
              onClick={() => {
                pressed.current = true;
                setInterrupted(false);
                setWanted(!running);
              }}
              size="xSmall"
              startIcon={running ? <PauseGlyph /> : <PlayGlyph />}
              variant="glass"
            />
          )}
        </div>

        {/* The wording and its buttons, rendered on the server and slotted in
            here. ⚠ THE BOX IS THIS COMPONENT'S EVEN WHERE THE CONTENT IS NOT:
            its reserved height is the room the wording rises into, so it has to
            sit inside the element `data-stage` is written on. */}
        <div className={styles.content} ref={contentRef}>
          {children}
        </div>
      </div>
    </div>
  );
}
export const MarqueeParallaxVideoClient = withSafeRender(MarqueeParallaxVideoClientImpl, "MarqueeParallaxVideoClient");
